import { nextTick, type ComponentInternalInstance } from "vue";
import { createI18n as _createI18n, useI18n as _useI18n } from "vue-i18n";
import type { WritableComputedRef } from "vue";
import type { I18n, Locale, Path, I18nOptions, Composer, VueI18n } from "vue-i18n";

/* Please note: BEWARE THE AT SYMBOL IN ALL VUE I18N FILES
 ** Error details: https://github.com/intlify/vue-i18n-next/issues/118
 ** This error has a nasty habit of only appearing on production
 */

// To add a new domain, create a new file in "vue-i18n/locales", named "/<some-domain>/<some-domain>.<some-locale>.json" then add the new domain name to "DOMAINS".
const DOMAINS: string[] = [
   "account",
   "authentication",
   "dna-testing",
   "meta",
   "privacy-and-terms",
   "redirects",
   "report",
   "study",
   "survey",
   "system",
   "user-data",
   "validation",
];

type I18nInstance = VueI18n | Composer<unknown, unknown, unknown, unknown, unknown, unknown>;
interface Messages {
   [domain: string]: { [key: string]: string };
}

// Default i18n settings:
const I18N_OPTIONS: I18nOptions = {
   legacy: false, // set 'legacy' to 'false' to use Composition API
   missing: (locale: Locale, key: Path, instance?: ComponentInternalInstance, type?: string): string | void => {
      //The vue-i18n MissingHandler - This method is triggered when a translation key is not found.
      // It allows you to define a fallback value to return when the requested path is invalid or the locale is unavailable.
      console.error(`i18n: path '${key}' for locale '${locale}' not found.`);
      return "";
   },
   silentTranslationWarn: import.meta.env.MODE === "production",
};

const createI18n = async (options: I18nOptions = I18N_OPTIONS): Promise<I18n> => {
   const i18n = _createI18n(options);

   return i18n;
};

const useI18n = () => {
   // Use i18n with global scope. You can call this method in the <script> of vue SFC to access messages like so t("authentication.login").
   const i18n = _useI18n({ useScope: "global" }); // includes t, te, locals

   return i18n; // you can return it with render context!
};

const setLocale = async (locale: string, _i18n: any | null = null): Promise<void> => {
   // Set the locale (or language, for example "en-gb" for GB English).
   const i18n = _i18n || useI18n();

   // load locale messages for the new locale
   await loadLocaleMessages(i18n, locale);

   // here is how you set the locale when vue-i18n is running in 'composition' mode (compatible with vue Composition API)
   (i18n.locale as WritableComputedRef<string>).value = locale;

   if (!import.meta.env.SSR) {
      // update HTML 'lang' attribute
      document.querySelector("html").setAttribute("lang", locale);
   }
};

const importLocaleMessages = async (domains: string[], locale: string): Promise<Messages> => {
   // Import all the messages for a given locale from the JSON files (files imported in parallel)
   const promiseMessages = domains.map(async (domain) => import(`./locales/${domain}/${domain}.${locale}.json`));
   // Resolve all the promises in parallel with Promise.allSettled
   const results = await Promise.allSettled(promiseMessages);

   /* By this point all promises are resolved, so we can check if these are 'fulfilled' or 'rejected'.
      A case in which a promise is 'rejected' is when the file imported does not exist. */
   const messages = {};
   domains.forEach((domain, i) => {
      // if ith the promise is fulfilled, add the resolved value to the 'message' object
      if (results[i].status === "fulfilled") {
         messages[domain] = (results[i] as PromiseFulfilledResult<any>).value;
      } else {
         // if the ith promise is rejected, console.error the domain and locale for which the import failed
         console.error(`Could not import messages for domain ${domain} and locale ${locale}.`);
      }
   });
   return messages;
};

const loadLocaleMessages = async (i18n: any, locale: string) => {
   /* Load all messages for a given locale the first time a locale is used.
   
      Example for how this works:
         1. The app opens in English. The messages for English are imported and added to the gloabl scope.
         2. The global locale is switched to Spanish. The messages for Spanish are imported and added to the gloabl scope.
         3. The locale is switched back to English. The messges for English are already there, so no new import is needed.
   */

   // if the messages for the given locale have already been imported, do not import these again.
   const existingMessages = await i18n.getLocaleMessage(locale);
   if (Object.keys(existingMessages).length === DOMAINS.length) {
      return null;
   }

   // import all messages from the JSON file
   const messages = await importLocaleMessages(DOMAINS, locale);

   // add the imported messages to the i18n global scope
   i18n.setLocaleMessage(locale, messages);

   return nextTick();
};

export { DOMAINS, createI18n, useI18n, setLocale, loadLocaleMessages };
export type { I18nInstance };
